// frontend/src/pages/historias/FormulariosDependientesPage.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ListaFormulariosConDependencias from '../../components/formularios/ListaFormulariosConDependencias';
import useFormulariosDependientes from '../../hooks/useFormulariosDependientes';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';

const FormulariosDependientesPage = () => {
  const { historiaId, especialidadId } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  const {
    loading,
    error,
    formulariosPadre,
    formulariosHijos,
    analisisProgreso,
    calcularEstadisticas
  } = useFormulariosDependientes(historiaId, especialidadId);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleCrearPlan = () => {
    // Navegar a la selección de formularios padre disponibles
    navigate(`/formularios/nuevo/plan/${historiaId}/${especialidadId}`);
  };
  
  // Calcular estadísticas generales
  const estadisticasGenerales = React.useMemo(() => {
    const stats = {
      totalPlanes: formulariosPadre.length,
      planesCerrados: 0,
      totalSeguimientos: 0,
      seguimientosActivos: 0,
      progresoPromedio: 0
    };
    
    let sumaPorcentajes = 0;
    let contadorConProgreso = 0;
    
    formulariosPadre.forEach(padre => {
      if (padre.estado === 'cerrado') stats.planesCerrados++;
      
      const hijos = formulariosHijos[padre.id] || [];
      stats.totalSeguimientos += hijos.length;
      
      if (hijos.length > 0) {
        const estadisticasPadre = calcularEstadisticas(padre, hijos);
        sumaPorcentajes += estadisticasPadre.porcentajeProgreso;
        contadorConProgreso++;
        
        if (estadisticasPadre.estado === 'en_progreso' || 
            estadisticasPadre.estado === 'avanzado') {
          stats.seguimientosActivos++;
        }
      }
    });
    
    if (contadorConProgreso > 0) {
      stats.progresoPromedio = Math.round(sumaPorcentajes / contadorConProgreso);
    }
    
    return stats;
  }, [formulariosPadre, formulariosHijos, calcularEstadisticas]);
  
  if (loading) {
    return <Loader />;
  }
  
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error al cargar los formularios: {error}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Box p={2}>
        <Breadcrumbs>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/dashboard')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" />
            Inicio
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/historias')}
          >
            Historias Clínicas
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/historias/${historiaId}`)}
          >
            Historia #{historiaId}
          </Link>
          <Typography color="text.primary">
            Formularios Terapéuticos
          </Typography>
        </Breadcrumbs>
      </Box>
      
      {/* Header */}
      <PageHeader 
        title="Gestión de Planes Terapéuticos y Seguimientos"
        subtitle="Administre los planes terapéuticos y sus seguimientos"
        icon={<TimelineIcon />}
      />
      
      {/* Estadísticas */}
      <Box p={3}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Planes
                </Typography>
                <Typography variant="h4">
                  {estadisticasGenerales.totalPlanes}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {estadisticasGenerales.planesCerrados} cerrados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Seguimientos
                </Typography>
                <Typography variant="h4">
                  {estadisticasGenerales.totalSeguimientos}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {estadisticasGenerales.seguimientosActivos} activos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Progreso Promedio
                </Typography>
                <Typography variant="h4" color={
                  estadisticasGenerales.progresoPromedio >= 70 ? 'success.main' :
                  estadisticasGenerales.progresoPromedio >= 40 ? 'warning.main' : 'text.primary'
                }>
                  {estadisticasGenerales.progresoPromedio}%
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  De objetivos completados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
              onClick={handleCrearPlan}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <AddIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6">
                  Nuevo Plan
                </Typography>
                <Typography variant="caption">
                  Crear plan terapéutico
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Tabs para diferentes vistas */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label="Todos los Formularios" 
              icon={<AssignmentIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Solo Planes Activos" 
              icon={<TimelineIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Requieren Atención" 
              icon={
                <Chip 
                  size="small" 
                  label={
                    formulariosPadre.filter(p => 
                      analisisProgreso[p.id]?.alertas?.length > 0
                    ).length
                  } 
                  color="error"
                />
              } 
              iconPosition="start"
            />
          </Tabs>
        </Paper>
        
        {/* Contenido según el tab seleccionado */}
        <Box role="tabpanel" hidden={tabValue !== 0}>
          <ListaFormulariosConDependencias
            historiaId={historiaId}
            especialidadId={especialidadId}
          />
        </Box>
        
        <Box role="tabpanel" hidden={tabValue !== 1}>
          {/* Filtrar solo planes con seguimientos activos */}
          <ListaFormulariosConDependencias
            historiaId={historiaId}
            especialidadId={especialidadId}
            filtro="activos"
          />
        </Box>
        
        <Box role="tabpanel" hidden={tabValue !== 2}>
          {/* Mostrar formularios que requieren atención */}
          <ListaFormulariosConDependencias
            historiaId={historiaId}
            especialidadId={especialidadId}
            filtro="atencion"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FormulariosDependientesPage;